// pkg71 — optional "Referral or invitation code" field on step 3 (D-71b/h): prefilled from ?ref= or ?invite=, live-validated via /check-code ("Referred by <firm>" / "Invitation code accepted" / non-blocking "not recognized"), sent as signup_code (server disambiguates); done screen reports granted invite credits and pending referral welcome credits; closes the pkg87 gap where invite_token had no UI — delivered 2026-07-16
// pkg84a — BOTH attestation checkboxes (pkg67 admin + pkg84 ToS) rendered broken inside .form-group: the global `.form-group input { width:100% … background }` rule blew the checkbox up into a giant beige box and `.form-group label { text-transform: uppercase }` squeezed ALL-CAPS text down the side (Ryan's screenshot). Fix: both blocks move OUT of .form-group into plain divs (no descendant rules apply) with explicit 16px checkbox sizing — delivered 2026-07-15
// pkg84 — REQUIRED Terms-of-Service acceptance (PRE-PILOT GATE): checkbox with links to /terms and /privacy gates the Create-firm button and handleSubmit; register sends terms_accepted (server re-enforces and stamps terms_version + terms_accepted_at on the firm) — delivered 2026-07-15
// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg67 — step 2 adds a REQUIRED practice-area chip picker (≥1, mirrors the jurisdiction picker); step 3 reframed as "firm administrator" with a required confirmation checkbox; register sends practice_areas + admin_confirmed — delivered 2026-07-12
// pkg63 — step 2 collects REQUIRED jurisdictions (multi-select of states, ≥1) instead of an optional free-text state; sends `jurisdictions` array — delivered 2026-07-10
import React, { useState, useEffect, useRef } from 'react';
import { publicApi } from '../utils/api';

// Firm signup wizard (Lawmighty Phase 2 Package 15 v2).
// Steps: 1) subdomain (live availability) → 2) firm details →
//        3) owner account → submit → "check your email" confirmation.
// On success the firm is pending_verification; the owner must click the
// emailed link (VerifyPage) before the firm activates and login works.

const ROOT_DOMAIN = process.env.REACT_APP_ROOT_DOMAIN || 'lawmighty.com';

// pkg63 — 50 states + DC (same list as AdminFirmSettingsPage). At least one
// is required: jurisdictions determine which state-specific built-in modules
// the firm receives, and can be changed later in Firm Settings.
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

// pkg67 — the 10 GLOBAL practice-area catalog entries, mirrored client-side
// exactly like US_STATES above (the signup page is unauthenticated, so it
// can't query the catalog; the server re-validates every key against
// practice_areas WHERE firm_id IS NULL). Keys/labels match the migrate.js
// seed (incl. the business_formation → business rename). Firm-custom areas
// are added later on the Practice Areas page, never at signup.
const PRACTICE_AREAS = [
  { key: 'family_law',       label: 'Family Law' },
  { key: 'estate_planning',  label: 'Estate Planning' },
  { key: 'real_estate',      label: 'Real Estate' },
  { key: 'criminal_defense', label: 'Criminal Defense' },
  { key: 'personal_injury',  label: 'Personal Injury' },
  { key: 'civil_litigation', label: 'Civil Litigation' },
  { key: 'business',         label: 'Business' },
  { key: 'employment',       label: 'Employment' },
  { key: 'immigration',      label: 'Immigration' },
  { key: 'bankruptcy',       label: 'Bankruptcy' },
];

export default function SignupPage() {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { email, subdomain } once registered

  // step 1
  const [subdomain, setSubdomain] = useState('');
  const [checking, setChecking] = useState(false);
  const [avail, setAvail] = useState(null); // { available, reason }
  const checkTimer = useRef(null);

  // step 2
  const [firmName, setFirmName] = useState('');
  const [jurisdictions, setJurisdictions] = useState([]); // pkg63 — ≥1 required
  const [practiceAreas, setPracticeAreas] = useState([]); // pkg67 — ≥1 required (keys)

  // step 3
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [ownerBar, setOwnerBar] = useState('');
  const [adminConfirmed, setAdminConfirmed] = useState(false); // pkg67
  const [termsAccepted, setTermsAccepted] = useState(false);   // pkg84

  // pkg71 (D-71b/h) — ONE optional code field serves both code kinds; the
  // server disambiguates (invite token vs. referral subdomain). Prefilled
  // from ?ref= or ?invite= (invite wins if both — it carries a credit grant).
  const [signupCode, setSignupCode] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      return (q.get('invite') || q.get('ref') || '').trim();
    } catch (_) { return ''; }
  });
  const [codeInfo, setCodeInfo] = useState(null); // { kind, referrer_name?, valid? }
  const [codeChecking, setCodeChecking] = useState(false);
  const codeTimer = useRef(null);

  // Debounced live code validation — purely informational; an unrecognized
  // code NEVER blocks the signup (the server drops it with a notice, D-71b).
  useEffect(() => {
    setCodeInfo(null);
    const code = signupCode.trim();
    if (!code) return;
    setCodeChecking(true);
    if (codeTimer.current) clearTimeout(codeTimer.current);
    codeTimer.current = setTimeout(async () => {
      try {
        const r = await publicApi.checkSignupCode(code);
        setCodeInfo(r);
      } catch (_) {
        setCodeInfo(null);
      } finally {
        setCodeChecking(false);
      }
    }, 400);
    return () => codeTimer.current && clearTimeout(codeTimer.current);
  }, [signupCode]);

  // Debounced live subdomain availability check.
  useEffect(() => {
    setAvail(null);
    if (!subdomain || subdomain.length < 3) return;
    setChecking(true);
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(async () => {
      try {
        const r = await publicApi.checkSubdomain(subdomain.toLowerCase().trim());
        setAvail(r);
      } catch (_) {
        setAvail(null);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => checkTimer.current && clearTimeout(checkTimer.current);
  }, [subdomain]);

  function next() { setError(''); setStep((s) => s + 1); }
  function back() { setError(''); setStep((s) => s - 1); }

  async function handleSubmit() {
    setError('');
    if (ownerPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!adminConfirmed) { setError('Please confirm you are the firm administrator.'); return; } // pkg67
    if (!termsAccepted) { setError('Please agree to the Terms of Service and Privacy Policy.'); return; } // pkg84
    setSubmitting(true);
    try {
      const r = await publicApi.registerFirm({
        subdomain: subdomain.toLowerCase().trim(),
        firm_name: firmName.trim(),
        jurisdictions, // pkg63 — validated ≥1 server-side too
        practice_areas: practiceAreas, // pkg67 — validated ≥1 + catalog server-side too
        owner_name: ownerName.trim(),
        owner_email: ownerEmail.trim(),
        owner_password: ownerPassword,
        owner_bar_number: ownerBar.trim() || null,
        admin_confirmed: true, // pkg67 — the checkbox gate above guarantees it
        terms_accepted: true,  // pkg84 — the required ToS checkbox guarantees it
        signup_code: signupCode.trim() || undefined, // pkg71 (D-71b/h) — invite token OR referral code; server disambiguates
      });
      setDone({ email: r.email, subdomain: r.subdomain, grantedCredits: r.grantedCredits || 0, referral: r.referral || null }); // pkg71
    } catch (err) {
      setError(err.message || 'Could not create your firm. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="login-page">
        <div className="login-box">
          <div className="login-brand"><h1>Almost there</h1><p>Confirm your email</p></div>
          <div className="alert alert-success">
            We sent a confirmation link to <strong>{done.email}</strong>. Click it to activate
            your firm at <strong>{done.subdomain}.{ROOT_DOMAIN}</strong> and sign in.
          </div>
          {/* pkg71 — honest code outcomes: invite grant landed now; referral
              welcome credits vest on the firm's first payment; an
              unrecognized code was dropped without blocking anything. */}
          {done.grantedCredits > 0 && (
            <p style={{ fontSize: 13, color: '#1d7a4f', marginTop: 12 }}>
              Your invitation added <strong>{done.grantedCredits} AI credits</strong> to your firm's wallet.
            </p>
          )}
          {done.referral && done.referral.applied && (
            <p style={{ fontSize: 13, color: '#555', marginTop: 12 }}>
              Referred by <strong>{done.referral.referrer_name}</strong> — you'll both receive AI credits
              after your firm's first payment.
            </p>
          )}
          {done.referral && done.referral.applied === false && (
            <p style={{ fontSize: 13, color: '#888', marginTop: 12 }}>{done.referral.notice}</p>
          )}
          <p style={{ fontSize: 13, color: '#888', marginTop: 16 }}>
            Didn't get it? Check spam, or{' '}
            <button type="button" className="linklike"
              onClick={() => publicApi.resendVerification(done.email, done.subdomain).catch(() => {})}>
              resend the link
            </button>.
          </p>
        </div>
      </div>
    );
  }

  const canNext1 = avail && avail.available;
  const canNext2 = firmName.trim().length > 0 && jurisdictions.length > 0 && practiceAreas.length > 0; // pkg63 + pkg67

  // pkg63 — chip helpers for the jurisdictions picker.
  function addJurisdiction(code) {
    if (!code) return;
    setJurisdictions(prev => (prev.includes(code) ? prev : [...prev, code]));
  }
  function removeJurisdiction(code) {
    setJurisdictions(prev => prev.filter(j => j !== code));
  }

  // pkg67 — chip helpers for the practice-areas picker (same pattern).
  function addPracticeArea(key) {
    if (!key) return;
    setPracticeAreas(prev => (prev.includes(key) ? prev : [...prev, key]));
  }
  function removePracticeArea(key) {
    setPracticeAreas(prev => prev.filter(k => k !== key));
  }
  function areaLabel(key) {
    const found = PRACTICE_AREAS.find(a => a.key === key);
    return found ? found.label : key;
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-brand">
          <h1>Create your firm</h1>
          <p>Step {step} of 3</p>
        </div>
        {error && <div className="alert alert-error">{error}</div>}

        {step === 1 && (
          <div>
            <div className="form-group">
              <label>Choose your address</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="text" value={subdomain} autoFocus
                  onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="yourfirm" style={{ flex: 1 }} />
                <span style={{ color: '#888', whiteSpace: 'nowrap' }}>.{ROOT_DOMAIN}</span>
              </div>
              {checking && <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Checking…</p>}
              {!checking && avail && avail.available && (
                <p style={{ fontSize: 12, color: '#2e7d32', marginTop: 6 }}>✓ {subdomain}.{ROOT_DOMAIN} is available</p>
              )}
              {!checking && avail && !avail.available && (
                <p style={{ fontSize: 12, color: '#c62828', marginTop: 6 }}>{avail.reason}</p>
              )}
            </div>
            <button className="btn btn-primary btn-block" disabled={!canNext1} onClick={next}>Continue</button>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="form-group">
              <label>Firm name</label>
              <input type="text" value={firmName} autoFocus
                onChange={(e) => setFirmName(e.target.value)} placeholder="Bright Law, PLLC" required />
            </div>
            <div className="form-group">
              <label>State(s) where your firm practices</label>
              {jurisdictions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {jurisdictions.map(code => (
                    <span key={code} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 13, fontFamily: 'Arial, sans-serif', fontWeight: 600,
                      padding: '4px 10px', borderRadius: 14,
                      background: '#f0faf5', color: '#1a3a2a', border: '1px solid #1a3a2a33',
                    }}>
                      {code}
                      <button type="button" onClick={() => removeJurisdiction(code)}
                        aria-label={`Remove ${code}`}
                        style={{ border: 'none', background: 'none', cursor: 'pointer',
                          color: '#0F1838', fontSize: 14, lineHeight: 1, padding: 0 }}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <select value="" onChange={(e) => addJurisdiction(e.target.value)}>
                <option value="">Add a state…</option>
                {US_STATES.filter(s => !jurisdictions.includes(s)).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                Select at least one. This controls which state-specific forms and modules
                appear for your firm — you can add or remove states later in Firm Settings.
              </p>
            </div>
            {/* pkg67 — practice-area picker, same chip pattern as jurisdictions */}
            <div className="form-group">
              <label>Practice area(s)</label>
              {practiceAreas.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {practiceAreas.map(key => (
                    <span key={key} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 13, fontFamily: 'Arial, sans-serif', fontWeight: 600,
                      padding: '4px 10px', borderRadius: 14,
                      background: '#f0f4ff', color: '#2f5496', border: '1px solid #2f549633',
                    }}>
                      {areaLabel(key)}
                      <button type="button" onClick={() => removePracticeArea(key)}
                        aria-label={`Remove ${areaLabel(key)}`}
                        style={{ border: 'none', background: 'none', cursor: 'pointer',
                          color: '#2f5496', fontSize: 14, lineHeight: 1, padding: 0 }}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <select value="" onChange={(e) => addPracticeArea(e.target.value)}>
                <option value="">Add a practice area…</option>
                {PRACTICE_AREAS.filter(a => !practiceAreas.includes(a.key)).map(a => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </select>
              <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                Select at least one. This sets up the case types and forms your firm starts
                with — you can change it anytime under Settings.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" onClick={back}>Back</button>
              <button className="btn btn-primary btn-block" disabled={!canNext2} onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="form-group">
              <label>Your name (firm administrator)</label>
              <input type="text" value={ownerName} autoFocus
                onChange={(e) => setOwnerName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Your email (firm administrator)</label>
              <input type="email" value={ownerEmail} autoComplete="email"
                onChange={(e) => setOwnerEmail(e.target.value)} required />
              <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                This is your sign-in and where your firm's notifications go, including
                client form submissions, until you change it in Firm Settings.
              </p>
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={ownerPassword} autoComplete="new-password"
                onChange={(e) => setOwnerPassword(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Bar number (optional)</label>
              <input type="text" value={ownerBar}
                onChange={(e) => setOwnerBar(e.target.value)} placeholder="BPR # 024983" />
            </div>
            {/* pkg71 (D-71b/h) — one optional field for both code kinds. The
                server disambiguates; an unrecognized code never blocks. */}
            <div className="form-group">
              <label>Referral or invitation code (optional)</label>
              <input type="text" value={signupCode}
                onChange={(e) => setSignupCode(e.target.value)}
                placeholder="Firm's code or your invitation" autoComplete="off" />
              {codeChecking && <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Checking…</p>}
              {!codeChecking && codeInfo && codeInfo.kind === 'referral' && (
                <p style={{ fontSize: 12, color: '#2e7d32', marginTop: 6 }}>
                  ✓ Referred by <strong>{codeInfo.referrer_name}</strong> — you'll both receive AI credits after your first payment.
                </p>
              )}
              {!codeChecking && codeInfo && codeInfo.kind === 'invite' && codeInfo.valid && (
                <p style={{ fontSize: 12, color: '#2e7d32', marginTop: 6 }}>✓ Invitation code accepted.</p>
              )}
              {!codeChecking && codeInfo && codeInfo.kind === 'invite' && !codeInfo.valid && (
                <p style={{ fontSize: 12, color: '#c62828', marginTop: 6 }}>This invitation code has expired or was already used.</p>
              )}
              {!codeChecking && codeInfo && codeInfo.kind === 'unknown' && (
                <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Code not recognized — you can still continue without it.</p>
              )}
            </div>
            {/* pkg67 — administrator attestation. Required (button gate below +
                server-side admin_confirmed check in routes/signup.js).
                pkg84a — plain div, NOT .form-group: the global .form-group
                input/label rules broke the checkbox layout. */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                padding: '10px 12px', background: '#F5F1E8', border: '1px solid #ddd',
                borderRadius: 6, fontWeight: 400,
              }}>
                <input type="checkbox" checked={adminConfirmed}
                  onChange={(e) => setAdminConfirmed(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16 }} />
                <span style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#555', lineHeight: 1.5 }}>
                  I confirm I am the administrator for{' '}
                  <strong style={{ color: '#0F1838' }}>{firmName.trim() || 'this firm'}</strong> and that{' '}
                  <strong style={{ color: '#0F1838' }}>{ownerEmail.trim() || 'the email above'}</strong> is my email address.
                </span>
              </label>
            </div>
            {/* pkg84 — REQUIRED terms acceptance. Links open in a new tab so
                the wizard state survives. Server re-enforces terms_accepted
                and records terms_version + terms_accepted_at on the firm. */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                padding: '10px 12px', background: '#F5F1E8', border: '1px solid #ddd',
                borderRadius: 6, fontWeight: 400,
              }}>
                <input type="checkbox" checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16 }} />
                <span style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#555', lineHeight: 1.5 }}>
                  On behalf of the firm, I have read and agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#1E2761', fontWeight: 600 }}>Terms of Service</a>{' '}
                  and the{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1E2761', fontWeight: 600 }}>Privacy Policy</a>.
                </span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" onClick={back} disabled={submitting}>Back</button>
              <button className="btn btn-primary btn-block" onClick={handleSubmit}
                disabled={submitting || !ownerName.trim() || !ownerEmail.trim() || !ownerPassword || !adminConfirmed || !termsAccepted}>
                {submitting ? <><span className="spinner" />Creating…</> : 'Create firm'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
